import { useEffect, useMemo, useState } from "react";
import { PushPin, Plus, TrashSimple, Warning, X } from "@phosphor-icons/react";
import {
  Button,
  DataRow,
  DataTable,
  Input,
  LoadingOverlay,
  ProfileTabs,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@breakingthelines/design-system";

import { handleTabArrowKeyDown } from "@/components/common/tab-keyboard-nav";
import {
  createEditorialOverride,
  deleteEditorialOverride,
  listEditorialOverrides,
  type EditorialOverride,
  type EntityTypeValue,
  type OverrideActionValue,
} from "@/lib/admin-api";

import styles from "./editorial-overrides.module.css";

// ENTITY_TYPE_* options a human is actually likely to override. The proto carries a few
// more (imported/moment/etc.) that don't need a place in this list.
const ENTITY_TYPE_OPTIONS: { value: EntityTypeValue; label: string }[] = [
  { value: "ENTITY_TYPE_ARTICLE", label: "Article" },
  { value: "ENTITY_TYPE_VIDEO", label: "Video" },
  { value: "ENTITY_TYPE_PODCAST", label: "Podcast" },
  { value: "ENTITY_TYPE_NEWSLETTER", label: "Newsletter" },
  { value: "ENTITY_TYPE_VISUAL", label: "Visual" },
  { value: "ENTITY_TYPE_THOUGHT", label: "Thought" },
  { value: "ENTITY_TYPE_COLLECTION", label: "Collection" },
  { value: "ENTITY_TYPE_USER", label: "Creator" },
  { value: "ENTITY_TYPE_SQUAD", label: "Squad" },
  { value: "ENTITY_TYPE_PLAYER", label: "Player" },
  { value: "ENTITY_TYPE_TEAM", label: "Club" },
  { value: "ENTITY_TYPE_COMPETITION", label: "Competition" },
  { value: "ENTITY_TYPE_COACH", label: "Manager" },
  { value: "ENTITY_TYPE_GAME", label: "Game" },
  { value: "ENTITY_TYPE_GAME_ROUND", label: "Game round" },
  { value: "ENTITY_TYPE_RATING", label: "Rating" },
  { value: "ENTITY_TYPE_PREDICTION", label: "Prediction" },
];

const ENTITY_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ENTITY_TYPE_OPTIONS.map((option) => [option.value, option.label]),
);

// The three ranking nudges: all multiply or divide a score that still competes with
// everything else in the result set. Different in degree, not in kind — grouped
// together, one tap apart, in a single row.
const ADJUST_ACTIONS: {
  value: OverrideActionValue;
  label: string;
  formula: string;
}[] = [
  { value: "OVERRIDE_ACTION_SUPPRESS", label: "Suppress", formula: "score ÷ (1 + weight)" },
  { value: "OVERRIDE_ACTION_BOOST", label: "Boost", formula: "score × (1 + weight)" },
  { value: "OVERRIDE_ACTION_FEATURE", label: "Feature", formula: "score × (1 + weight × 1.5)" },
];

const PIN_ACTION: OverrideActionValue = "OVERRIDE_ACTION_PIN";

const ACTION_LABELS: Record<string, string> = {
  OVERRIDE_ACTION_BOOST: "Boost",
  OVERRIDE_ACTION_SUPPRESS: "Suppress",
  OVERRIDE_ACTION_FEATURE: "Feature",
  OVERRIDE_ACTION_PIN: "Pin to top",
};

type DurationPreset = { label: string; seconds: number; long?: boolean };

const DURATION_PRESETS: DurationPreset[] = [
  { label: "1 hour", seconds: 3600 },
  { label: "6 hours", seconds: 6 * 3600 },
  { label: "1 day", seconds: 24 * 3600 },
  { label: "3 days", seconds: 3 * 24 * 3600 },
  { label: "1 week", seconds: 7 * 24 * 3600 },
  { label: "2 weeks", seconds: 14 * 24 * 3600, long: true },
  { label: "1 month", seconds: 30 * 24 * 3600, long: true },
];

const TABS = ["Active", "All"] as const;
type Tab = (typeof TABS)[number];

function formatDateTime(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function isExpired(override: EditorialOverride): boolean {
  if (!override.expiresAt) return false;
  const t = new Date(override.expiresAt).getTime();
  return !Number.isNaN(t) && t <= Date.now();
}

function actionToneClass(action: OverrideActionValue): string {
  switch (action) {
    case "OVERRIDE_ACTION_PIN":
      return styles.actionPin;
    case "OVERRIDE_ACTION_SUPPRESS":
      return styles.actionSuppress;
    case "OVERRIDE_ACTION_FEATURE":
      return styles.actionFeature;
    default:
      return styles.actionBoost;
  }
}

function outcomeCell(override: EditorialOverride): {
  text: string;
  tone: "neutral" | "up" | "down";
} {
  if (override.outcomeTracked) {
    const delta = override.outcomeDelta ?? 0;
    const sign = delta > 0 ? "+" : "";
    return {
      text: `${sign}${delta.toFixed(1)} engagement`,
      tone: delta > 0 ? "up" : delta < 0 ? "down" : "neutral",
    };
  }
  if (isExpired(override)) {
    return { text: "Pending measurement", tone: "neutral" };
  }
  return { text: "Measures once it ends", tone: "neutral" };
}

type FormState = {
  entityId: string;
  entityType: EntityTypeValue | "";
  action: OverrideActionValue | "";
  weight: string;
  durationSeconds: number | null;
  reason: string;
  pinConfirmed: boolean;
};

const EMPTY_FORM: FormState = {
  entityId: "",
  entityType: "",
  action: "",
  weight: "1",
  durationSeconds: null,
  reason: "",
  pinConfirmed: false,
};

export function EditorialOverridesPage() {
  const [activeTab, setActiveTab] = useState<Tab>("Active");
  const [overrides, setOverrides] = useState<EditorialOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listEditorialOverrides({ activeOnly: activeTab === "Active", pageSize: 100 })
      .then((data) => {
        if (cancelled) return;
        setOverrides(data.overrides ?? []);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? "Failed to load editorial overrides");
        setOverrides([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, reloadKey]);

  const isPin = form.action === PIN_ACTION;
  const canSubmit = useMemo(() => {
    if (!form.entityId.trim() || !form.entityType || !form.action) return false;
    if (!form.durationSeconds) return false;
    if (!form.reason.trim()) return false;
    if (isPin && !form.pinConfirmed) return false;
    return true;
  }, [form, isPin]);

  function resetForm() {
    setForm(EMPTY_FORM);
    setCreateError(null);
  }

  // Closing the panel always discards whatever was entered, including a PIN confirmation —
  // reopening "New override" must never resurrect a half-filled pin. Shared by the header
  // Close toggle and the in-panel Cancel button so they can't drift apart.
  function closeForm() {
    setFormOpen(false);
    resetForm();
  }

  async function handleCreate() {
    if (!canSubmit || !form.entityType || !form.action || !form.durationSeconds) return;
    setCreating(true);
    setCreateError(null);
    try {
      await createEditorialOverride({
        entityId: form.entityId.trim(),
        entityType: form.entityType,
        action: form.action,
        weight: isPin ? 1 : Number(form.weight) || 1,
        reason: form.reason.trim(),
        durationSeconds: form.durationSeconds,
      });
      closeForm();
      setActiveTab("Active");
      setReloadKey((v) => v + 1);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create override");
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(override: EditorialOverride) {
    const label = `${ACTION_LABELS[override.action] ?? override.action} on ${override.entityId}`;
    if (
      !window.confirm(`Remove this override now?\n\n${label}\n\nThis takes effect immediately.`)
    ) {
      return;
    }
    setDeletingId(override.id);
    setError(null);
    try {
      await deleteEditorialOverride(override.id);
      setOverrides((prev) => prev.filter((o) => o.id !== override.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove override");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Search overrides</h1>
          <p className={styles.subtitle}>
            Manual boosts, suppressions, features, and pins applied to search ranking. Every
            override expires on its own — nothing here is permanent unless it&apos;s renewed.
          </p>
        </div>
        <Button
          onClick={() => {
            if (formOpen) {
              closeForm();
            } else {
              setFormOpen(true);
            }
          }}
        >
          {formOpen ? (
            <>
              <X size={14} weight="bold" /> Close
            </>
          ) : (
            <>
              <Plus size={14} weight="bold" /> New override
            </>
          )}
        </Button>
      </div>

      {formOpen ? (
        <section className={styles.createPanel}>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Entity ID</span>
              <Input
                placeholder="Content, thought, player, club ID…"
                value={form.entityId}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  setForm((f) => ({ ...f, entityId: value }));
                }}
              />
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>Entity type</span>
              <Select
                value={form.entityType}
                onValueChange={(value: string) =>
                  setForm((f) => ({ ...f, entityType: value as EntityTypeValue }))
                }
              >
                <SelectTrigger>
                  <SelectValue>
                    {(value: string | null) =>
                      value ? (ENTITY_TYPE_LABELS[value] ?? value) : "Select a type"
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>

          <div className={styles.fieldBlock}>
            <span className={styles.fieldLabel}>Action</span>
            <p className={styles.fieldHint}>
              Boost, suppress, and feature are ranking nudges — the entity still competes with
              everything else. Pin is not a stronger version of these. It skips ranking entirely.
            </p>
            <div className={styles.actionRow}>
              {ADJUST_ACTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-label={option.label}
                  aria-pressed={form.action === option.value}
                  className={`${styles.actionChip} ${
                    form.action === option.value ? styles.actionChipActive : ""
                  }`}
                  onClick={() =>
                    setForm((f) => ({ ...f, action: option.value, pinConfirmed: false }))
                  }
                >
                  <span className={styles.actionChipLabel}>{option.label}</span>
                  <span className={styles.actionChipFormula}>{option.formula}</span>
                </button>
              ))}
            </div>

            <div className={styles.pinRow}>
              <button
                type="button"
                aria-label="Pin to top"
                aria-pressed={form.action === PIN_ACTION}
                className={`${styles.pinChip} ${form.action === PIN_ACTION ? styles.pinChipActive : ""}`}
                onClick={() => setForm((f) => ({ ...f, action: PIN_ACTION }))}
              >
                <PushPin size={14} weight="fill" />
                <span className={styles.actionChipLabel}>Pin to top</span>
                <span className={styles.actionChipFormula}>
                  Unconditional. Placed first, ignoring rank, until it expires.
                </span>
              </button>
            </div>

            {isPin ? (
              <label className={styles.pinConfirm}>
                <input
                  type="checkbox"
                  checked={form.pinConfirmed}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setForm((f) => ({ ...f, pinConfirmed: checked }));
                  }}
                />
                <span>
                  <Warning size={14} weight="fill" className={styles.pinConfirmIcon} />I understand
                  this overrides organic ranking entirely. Readers see this first regardless of
                  relevance, quality, or what else is trending.
                </span>
              </label>
            ) : null}
          </div>

          {form.action && form.action !== PIN_ACTION ? (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Weight multiplier</span>
              <Input
                type="number"
                min="0.1"
                step="0.1"
                value={form.weight}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  setForm((f) => ({ ...f, weight: value }));
                }}
                className={styles.weightInput}
              />
              <span className={styles.fieldHint}>
                Default is 1. Higher weight, stronger effect.
              </span>
            </label>
          ) : null}

          <div className={styles.fieldBlock}>
            <span className={styles.fieldLabel}>Duration</span>
            <p className={styles.fieldHint}>
              Every override expires on its own — pick how long. There&apos;s no default; choose
              deliberately.
            </p>
            <div className={styles.durationRow}>
              {DURATION_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  className={`${styles.durationChip} ${
                    form.durationSeconds === preset.seconds ? styles.durationChipActive : ""
                  } ${preset.long ? styles.durationChipLong : ""}`}
                  onClick={() => setForm((f) => ({ ...f, durationSeconds: preset.seconds }))}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Reason</span>
            <Textarea
              placeholder="Why? This is the only record of why this override exists."
              value={form.reason}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setForm((f) => ({ ...f, reason: value }));
              }}
              rows={3}
            />
          </label>

          {createError ? (
            <p className={styles.formError} role="alert">
              {createError}
            </p>
          ) : null}

          <div className={styles.formActions}>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleCreate()}
              disabled={!canSubmit || creating}
              className={isPin ? styles.submitPin : undefined}
            >
              {creating
                ? isPin
                  ? "Pinning…"
                  : "Applying…"
                : isPin
                  ? "Confirm pin to top"
                  : form.action
                    ? `Apply ${ACTION_LABELS[form.action]}`
                    : "Create override"}
            </Button>
          </div>
        </section>
      ) : null}

      <div className={styles.toolbar}>
        <ProfileTabs
          role="tablist"
          aria-label="Overrides"
          tabs={TABS.map((tab) => ({ id: tab, label: tab }))}
          activeTab={activeTab}
          className={styles.tabs}
          onKeyDown={handleTabArrowKeyDown}
          onTabChange={(tab) => setActiveTab(tab as Tab)}
        />
      </div>

      <section className={styles.tableSection}>
        {loading ? <LoadingOverlay label="Loading overrides..." /> : null}

        <DataTable
          columns="minmax(180px, 1.2fr) minmax(150px, 1fr) minmax(240px, 1.8fr) minmax(130px, 0.9fr) minmax(150px, 1fr) minmax(48px, 0.4fr)"
          header={["Entity", "Action", "Reason", "Expires", "Outcome", ""]}
          headerClassName={styles.tableHeader}
        >
          {overrides.map((override) => {
            const outcome = outcomeCell(override);
            const expired = isExpired(override);
            return (
              <DataRow key={override.id} className={styles.tableRow}>
                <div className={styles.entityCell}>
                  <span className={styles.entityType}>
                    {ENTITY_TYPE_LABELS[override.entityType] ?? override.entityType}
                  </span>
                  <span className={styles.entityId}>{override.entityId}</span>
                </div>

                <div className={styles.actionCell}>
                  <span className={`${styles.actionBadge} ${actionToneClass(override.action)}`}>
                    {override.action === "OVERRIDE_ACTION_PIN" ? (
                      <PushPin size={11} weight="fill" />
                    ) : null}
                    {ACTION_LABELS[override.action] ?? override.action}
                  </span>
                  {override.action !== "OVERRIDE_ACTION_PIN" ? (
                    <span className={styles.weightLabel}>×{override.weight.toFixed(1)}</span>
                  ) : null}
                </div>

                <div className={styles.reasonCell}>
                  <span className={styles.reasonText} title={override.reason}>
                    {override.reason || "—"}
                  </span>
                  <span className={styles.reasonMeta}>
                    by {override.createdBy} · {formatDateTime(override.createdAt)}
                  </span>
                </div>

                <div className={styles.expiresCell} data-label="Expires">
                  <span className={expired ? styles.expiredLabel : styles.meta}>
                    {expired ? "Expired " : ""}
                    {formatDateTime(override.expiresAt)}
                  </span>
                </div>

                <span
                  className={`${styles.outcomeText} ${
                    outcome.tone === "up"
                      ? styles.outcomeUp
                      : outcome.tone === "down"
                        ? styles.outcomeDown
                        : ""
                  }`}
                  data-label="Outcome"
                >
                  {outcome.text}
                </span>

                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    disabled={deletingId === override.id}
                    onClick={() => void handleDelete(override)}
                    aria-label={`Remove override on ${override.entityId}`}
                  >
                    <TrashSimple size={14} />
                  </button>
                </div>
              </DataRow>
            );
          })}
        </DataTable>

        {!loading && error ? <div className={styles.emptyState}>{error}</div> : null}
        {!loading && !error && overrides.length === 0 ? (
          <div className={styles.emptyState}>
            {activeTab === "Active"
              ? "No active overrides. Search ranking is fully organic right now."
              : "No overrides yet."}
          </div>
        ) : null}
      </section>
    </div>
  );
}
